/**
 * Entry point — wraps the app with MUI ThemeProvider and React Query.
 *
 * Theme mode (light/dark) is managed by ThemeModeProvider and persisted
 * in localStorage. The ThemedApp component bridges the context to MUI's
 * ThemeProvider via useMemo.
 */

import React, { useMemo } from "react";
import ReactDOM from "react-dom/client";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import "./i18n"; // initialize i18next before any component renders (I18N_PLAN.md 0.1)
import App from "./App";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider } from "./hooks/useAuth";
import { ThemeModeProvider, useThemeMode } from "./hooks/useThemeMode";
import { buildTheme } from "./theme";
import { initAnalytics } from "./utils/initAnalytics";
import { initSentry } from "./utils/initSentry";

// Initialize observability before render. Both are no-ops without their env keys
// (REACT_APP_SENTRY_DSN / REACT_APP_POSTHOG_KEY) — dev/unconfigured make no calls.
// Sentry first so error tracking is live before anything else runs.
initSentry();
initAnalytics();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30s — companies/questions don't change often
      retry: 1,
    },
  },
});

/**
 * Inner component that reads the theme mode from context and builds
 * the MUI theme. Separated so useMemo re-runs only when mode changes.
 */
function ThemedApp() {
  const { mode } = useThemeMode();
  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <AuthProvider>
          <App />
        </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeModeProvider>
        <ThemedApp />
      </ThemeModeProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
